<template>
    <div class="flex items-center gap-3 border-b border-gray-200 px-6 py-3">
        <AiChatRobot :size="46" :interactive="false" class="shrink-0" />
        <div class="min-w-0 flex-1">
            <div v-if="editing" class="flex items-center gap-1">
                <input
                    v-model="editName"
                    type="text"
                    class="min-w-0 flex-1 rounded-md border border-gray-300 px-2 py-1 text-sm focus:border-blue-400 focus:outline-hidden"
                    @keyup.enter="save"
                    @keyup.esc="cancel"
                />
                <button
                    type="button"
                    class="shrink-0 cursor-pointer text-green-600 hover:text-green-700"
                    @click="save"
                >
                    <CheckIcon class="h-4 w-4" />
                </button>
                <button
                    type="button"
                    class="shrink-0 cursor-pointer text-gray-400 hover:text-gray-600"
                    @click="cancel"
                >
                    <XMarkIcon class="h-4 w-4" />
                </button>
            </div>
            <div v-else class="group flex items-center gap-1.5">
                <p class="truncate text-base font-semibold text-gray-800">
                    {{ title }}
                </p>
                <button
                    v-if="chatId !== null"
                    type="button"
                    class="shrink-0 cursor-pointer text-gray-400 opacity-0 transition hover:text-gray-700 group-hover:opacity-100"
                    :title="t('vue.ai_chat_rename')"
                    @click="start"
                >
                    <PencilSquareIcon class="h-4 w-4" />
                </button>
                <button
                    v-if="chatId !== null && canDelete"
                    type="button"
                    class="shrink-0 cursor-pointer text-gray-400 opacity-0 transition hover:text-red-600 group-hover:opacity-100"
                    :title="t('vue.ai_chat_delete')"
                    @click="deleteChat"
                >
                    <TrashIcon class="h-4 w-4" />
                </button>
            </div>
            <p v-if="createdAt" class="mt-0.5 text-xs text-gray-400">
                {{ formatDateTimeEu(createdAt) }}
            </p>
        </div>

        <div class="ml-auto flex shrink-0 items-center gap-2">
            <AiChatFeedbackButtons
                v-if="chatId !== null"
                :chat-id="chatId"
                :feedback="chatFeedback"
                :disabled="readOnly"
                @change="chatFeedback = $event"
            />
            <AiChatCapabilitiesButton
                :disabled="readOnly"
                @select="$emit('select-capability', $event)"
            />
            <div v-if="chatId !== null">
                <button
                    ref="buttonRef"
                    type="button"
                    class="inline-flex cursor-pointer items-center gap-1.5 rounded-lg border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-600 transition hover:bg-gray-50"
                    @click="toggleMenu"
                >
                    <ArrowDownTrayIcon class="h-4 w-4" />
                    {{ t("vue.ai_chat_export") }}
                </button>

                <!-- Teleported to body so the overflow-hidden chat panel never clips it. -->
                <Teleport to="body">
                    <template v-if="menuOpen">
                        <div
                            class="fixed inset-0 z-[590]"
                            @click="menuOpen = false"
                        />
                        <div
                            class="fixed z-[600] w-44 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg"
                            :style="menuStyle"
                        >
                            <a
                                v-for="option in exportOptions"
                                :key="option.format"
                                :href="hrefFor(option.format)"
                                :target="option.newTab ? '_blank' : undefined"
                                rel="noopener"
                                class="block px-4 py-2 text-sm text-gray-700 transition hover:bg-gray-50"
                                @click="menuOpen = false"
                            >
                                {{ option.label }}
                            </a>
                        </div>
                    </template>
                </Teleport>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import { router, usePage } from "@inertiajs/vue3";
import route from "ziggy-js";
import type { IRhizomPageProps } from "@/types/RhizomPageProps/RhizomPageProps";
import {
    ArrowDownTrayIcon,
    PencilSquareIcon,
    TrashIcon,
    CheckIcon,
    XMarkIcon,
} from "@heroicons/vue/24/outline";
import { useTranslations } from "@/composables/useTranslations";
import { formatDateTimeEu } from "@/utils/formatDateTimeEu";
import useConfirm from "@/composables/useConfirm";
import type { IAiChatFeedback } from "@/types/AiChat/IAiChat";
import AiChatFeedbackButtons from "@/Pages/AiChat/components/AiChatFeedbackButtons.vue";

const props = withDefaults(
    defineProps<{
        chatId?: number | null;
        title?: string;
        createdAt?: string | null;
        feedback?: IAiChatFeedback | null;
    }>(),
    { chatId: null, title: "", createdAt: null, feedback: null },
);

defineEmits<{
    (event: "select-capability", prompt: string): void;
}>();

// Held locally for the same reason the message bubble holds its own: the server
// already stored the verdict, this only keeps the thumb lit until the next load.
const chatFeedback = ref<IAiChatFeedback | null>(props.feedback ?? null);

watch(
    () => props.feedback,
    (value) => {
        chatFeedback.value = value ?? null;
    },
);

// A locked trial chat is read-only, and so is rating it — mirrors the server gate.
const readOnly = computed(
    () => usePage<IRhizomPageProps>().props.aiChat?.can_write === false,
);

const { t } = useTranslations();
const { show } = useConfirm();

// Trial (limited) teams may not delete chats — deletion cascades messages and
// would reset the trial message cap. Enforced server-side by AiChatPolicy.
const canDelete = computed(
    () => usePage<IRhizomPageProps>().props.aiChat?.level !== "limited",
);

const editing = ref(false);
const editName = ref("");

const start = (): void => {
    editing.value = true;
    editName.value = props.title;
};

const cancel = (): void => {
    editing.value = false;
};

const save = (): void => {
    if (props.chatId === null || editName.value.trim() === "") {
        return;
    }
    router.put(
        route("ai-chat.update", { chat: props.chatId }),
        { title: editName.value.trim() },
        {
            preserveScroll: true,
            preserveState: true,
            onSuccess: () => {
                editing.value = false;
            },
        },
    );
};

const deleteChat = async (): Promise<void> => {
    if (props.chatId === null) {
        return;
    }
    const confirmed = await show({
        header: t("vue.ai_chat_delete"),
        message: t("vue.ai_chat_delete_confirm"),
        type: "danger",
        confirm: t("vue.ai_chat_delete"),
    });

    if (confirmed) {
        router.delete(route("ai-chat.destroy", { chat: props.chatId }));
    }
};

const menuOpen = ref(false);
const buttonRef = ref<HTMLElement | null>(null);
const menuStyle = ref<Record<string, string>>({});

const toggleMenu = (): void => {
    if (menuOpen.value) {
        menuOpen.value = false;
        return;
    }
    const rect = buttonRef.value?.getBoundingClientRect();
    if (rect) {
        menuStyle.value = {
            top: `${rect.bottom + 4}px`,
            right: `${window.innerWidth - rect.right}px`,
        };
    }
    menuOpen.value = true;
};

const exportOptions = computed(() => [
    { format: "pdf", label: t("vue.ai_chat_export_pdf"), newTab: true },
    { format: "html", label: t("vue.ai_chat_export_html"), newTab: true },
    { format: "json", label: t("vue.ai_chat_export_json"), newTab: false },
]);

const hrefFor = (format: string): string =>
    route("ai-chat.export", { chat: props.chatId ?? 0, format });
</script>
